import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, ArrowDown, ArrowRight } from "@phosphor-icons/react";
import { useApkDownload } from "@/lib/useApkDownload";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Rates", href: "#rates" },
  { label: "Security", href: "#security" },
  { label: "FAQ", href: "#faqs" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { downloading, start } = useApkDownload();

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-line/70 bg-night/80 backdrop-blur-xl" data-testid="site-header">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <a href="#home" className="logo-chip" data-testid="header-logo" aria-label="Rupora home">
            <img src="/assets/logo-wordmark.png" alt="Rupora" className="h-6 w-auto sm:h-7" />
          </a>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-mist transition-colors duration-300 hover:text-brand-300"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href="/login"
              data-testid="header-get-started-button"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-brand-500/35 hover:brightness-110"
            >
              Get Started
              <ArrowRight size={15} weight="bold" />
            </a>
          </div>

          <button
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-pine text-paper lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <List size={20} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[70] bg-night/70 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              data-testid="drawer-overlay"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-y-0 left-0 z-[75] flex w-[300px] max-w-[85vw] flex-col border-r border-line bg-pine"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between border-b border-line px-6 py-5">
                <span className="logo-chip">
                  <img src="/assets/logo-wordmark.png" alt="Rupora" className="h-6 w-auto" />
                </span>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="drawer-close-button"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-mist transition-colors hover:text-paper"
                >
                  <X size={18} />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-4 py-5">
                {NAV.map((item, i) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.35 }}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="mb-1.5 flex items-center justify-between rounded-2xl px-5 py-4 text-base font-medium text-paper/85 transition-colors hover:bg-night/60 hover:text-brand-300"
                  >
                    {item.label}
                    <ArrowRight size={16} className="text-mist/50" />
                  </motion.a>
                ))}
              </nav>

              <div className="space-y-2.5 border-t border-line px-5 py-5">
                <a
                  href="/login"
                  data-testid="mobile-get-started-button"
                  className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-brand-500 to-brand-600 px-5 py-3.5 text-center text-sm font-semibold text-white shadow-lg shadow-brand-500/25"
                >
                  Get Started <ArrowRight size={15} weight="bold" />
                </a>
                <button
                  onClick={start}
                  disabled={downloading}
                  data-testid="mobile-download-button"
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-line px-5 py-3.5 text-center text-sm font-semibold text-paper/85 disabled:opacity-70"
                >
                  <ArrowDown size={15} weight="bold" />
                  {downloading ? "Downloading..." : "Download App"}
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
